import React, { useState, useEffect } from 'react';
import { servicesAPI, bookingsAPI, providersAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

const categories = ['Plumber', 'Electrician', 'Cleaner', 'Tutor', 'Beautician', 'Carpenter', 'Painter', 'AC Repair'];
const cities = ['Islamabad', 'Rawalpindi', 'Lahore', 'Karachi', 'Peshawar', 'Multan', 'Faisalabad'];
const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const statusColors = {
  pending: { bg: '#FFF7ED', color: '#D97706', label: 'Pending' },
  confirmed: { bg: '#EFF6FF', color: '#1D4ED8', label: 'Confirmed' },
  in_progress: { bg: '#F0FDF4', color: '#16A34A', label: 'In Progress' },
  completed: { bg: '#F0FDF4', color: '#16A34A', label: 'Completed' },
  cancelled: { bg: '#FEF2F2', color: '#DC2626', label: 'Cancelled' },
};

const emptyService = { title: '', category: '', description: '', price: '', priceType: 'per_visit', city: '', availability: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'] };

const ProviderDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showForm, setShowForm] = useState(false);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, servicesRes, bookingsRes] = await Promise.all([
        providersAPI.getDashboard(),
        servicesAPI.getMyServices(),
        bookingsAPI.getMy(),
      ]);
      setStats(statsRes.data);
      setServices(servicesRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {}
    setLoading(false);
  };

  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await servicesAPI.update(editingId, serviceForm);
      } else {
        await servicesAPI.create(serviceForm);
      }
      setShowForm(false);
      setServiceForm(emptyService);
      setEditingId(null);
      fetchAll();
    } catch (err) { alert('Failed to save service.'); }
  };

  const handleEdit = (service) => {
    setServiceForm({ title: service.title, category: service.category, description: service.description, price: service.price, priceType: service.priceType, city: service.city, availability: service.availability });
    setEditingId(service._id);
    setShowForm(true);
    setActiveTab('services');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this service?')) return;
    await servicesAPI.delete(id);
    fetchAll();
  };

  const updateBookingStatus = async (id, status) => {
    await bookingsAPI.updateStatus(id, status);
    fetchAll();
  };

  const toggleAvailability = (day) => {
    const avail = serviceForm.availability.includes(day)
      ? serviceForm.availability.filter(d => d !== day)
      : [...serviceForm.availability, day];
    setServiceForm({ ...serviceForm, availability: avail });
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1>Provider Dashboard 🔧</h1>
            <p>Welcome back, {user?.name} — manage your services and bookings</p>
          </div>
          <button className="new-booking-btn" onClick={() => { setShowForm(true); setActiveTab('services'); setEditingId(null); setServiceForm(emptyService); }}>
            + Add New Service
          </button>
        </div>

        {/* Stats */}
        <div className="stats-row">
          <div className="stat-card"><div className="stat-num">{stats.myServices || 0}</div><div className="stat-lbl">My Services</div></div>
          <div className="stat-card pending"><div className="stat-num">{stats.pendingBookings || 0}</div><div className="stat-lbl">Pending Bookings</div></div>
          <div className="stat-card success"><div className="stat-num">{stats.completedBookings || 0}</div><div className="stat-lbl">Completed</div></div>
          <div className="stat-card blue"><div className="stat-num">Rs. {(stats.totalEarnings || 0).toLocaleString()}</div><div className="stat-lbl">Total Earnings</div></div>
        </div>

        {/* Tabs */}
        <div className="dash-tabs">
          {['overview', 'services', 'bookings'].map(tab => (
            <button key={tab} className={`dash-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <h3 style={{ fontSize: '17px', fontWeight: '600', color: 'var(--gray-700)', marginBottom: '1rem' }}>Recent Bookings</h3>
            {bookings.slice(0, 5).map(booking => {
              const s = statusColors[booking.status] || statusColors.pending;
              return (
                <div key={booking._id} className="booking-item" style={{ marginBottom: '10px' }}>
                  <div className="bi-left">
                    <div className="bi-avatar">{booking.customer?.name?.charAt(0) || 'C'}</div>
                  </div>
                  <div className="bi-body">
                    <div className="bi-top">
                      <div>
                        <div className="bi-service">{booking.service?.title}</div>
                        <div className="bi-provider">Customer: {booking.customer?.name}</div>
                        <div className="bi-meta">📅 {new Date(booking.bookingDate).toLocaleDateString()} &nbsp;•&nbsp; 🕐 {booking.timeSlot} &nbsp;•&nbsp; 📍 {booking.city}</div>
                      </div>
                      <div className="bi-right">
                        <span className="bi-status" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                        <div className="bi-amount">Rs. {booking.providerEarning?.toFixed(0)}</div>
                        <div className="bi-commission">After 15% fee</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div>
            {showForm && (
              <div className="add-service-form">
                <h3>{editingId ? 'Edit Service' : 'Add New Service'}</h3>
                <form onSubmit={handleServiceSubmit}>
                  <div className="form-grid">
                    <div className="form-field full">
                      <label>Service Title</label>
                      <input type="text" placeholder="e.g. Professional Plumbing Repair" value={serviceForm.title}
                        onChange={e => setServiceForm({ ...serviceForm, title: e.target.value })} required />
                    </div>
                    <div className="form-field">
                      <label>Category</label>
                      <select value={serviceForm.category} onChange={e => setServiceForm({ ...serviceForm, category: e.target.value })} required>
                        <option value="">Select Category</option>
                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>City</label>
                      <select value={serviceForm.city} onChange={e => setServiceForm({ ...serviceForm, city: e.target.value })} required>
                        <option value="">Select City</option>
                        {cities.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Price (Rs.)</label>
                      <input type="number" placeholder="e.g. 1000" value={serviceForm.price}
                        onChange={e => setServiceForm({ ...serviceForm, price: e.target.value })} required />
                    </div>
                    <div className="form-field">
                      <label>Price Type</label>
                      <select value={serviceForm.priceType} onChange={e => setServiceForm({ ...serviceForm, priceType: e.target.value })}>
                        <option value="per_visit">Per Visit</option>
                        <option value="per_hour">Per Hour</option>
                        <option value="fixed">Fixed Price</option>
                      </select>
                    </div>
                    <div className="form-field full">
                      <label>Description</label>
                      <textarea rows={3} placeholder="Describe your service in detail..." value={serviceForm.description}
                        onChange={e => setServiceForm({ ...serviceForm, description: e.target.value })} required />
                    </div>
                    <div className="form-field full">
                      <label>Available Days</label>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {days.map(day => (
                          <button key={day} type="button"
                            style={{ padding: '5px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', border: '1.5px solid', borderColor: serviceForm.availability.includes(day) ? 'var(--primary)' : 'var(--gray-200)', background: serviceForm.availability.includes(day) ? 'var(--primary-light)' : 'white', color: serviceForm.availability.includes(day) ? 'var(--primary)' : 'var(--gray-600)' }}
                            onClick={() => toggleAvailability(day)}>{day.slice(0, 3)}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="submit-btn">{editingId ? 'Update Service' : 'Add Service'}</button>
                    <button type="button" className="cancel-btn" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
                  </div>
                </form>
              </div>
            )}

            {loading ? <div className="dash-loading">Loading services...</div> :
              services.length === 0 ? (
                <div className="dash-empty">
                  <div className="empty-icon">🔧</div>
                  <h3>No services yet</h3>
                  <p>Add your first service to start getting bookings</p>
                </div>
              ) : (
                <div className="provider-services-grid">
                  {services.map(service => (
                    <div key={service._id} className="ps-card">
                      <div className="ps-card-top">
                        <div>
                          <div className="ps-title">{service.title}</div>
                          <div className="ps-cat">{service.category} • {service.city}</div>
                        </div>
                        <span style={{ background: 'var(--primary-light)', color: 'var(--primary)', fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px' }}>
                          {service.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="ps-price">Rs. {service.price} <span style={{ fontSize: '12px', color: 'var(--gray-500)', fontWeight: '400' }}>/{service.priceType?.replace('_', ' ')}</span></div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>⭐ {service.rating?.toFixed(1) || 'No ratings'} ({service.totalReviews || 0} reviews)</div>
                      <div className="ps-actions">
                        <button className="ps-edit" onClick={() => handleEdit(service)}>✏️ Edit</button>
                        <button className="ps-delete" onClick={() => handleDelete(service._id)}>🗑️ Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="bookings-list">
            {bookings.length === 0 ? (
              <div className="dash-empty">
                <div className="empty-icon">📋</div>
                <h3>No bookings yet</h3>
                <p>Bookings will appear here once customers book your services</p>
              </div>
            ) : bookings.map(booking => {
              const s = statusColors[booking.status] || statusColors.pending;
              return (
                <div key={booking._id} className="booking-item">
                  <div className="bi-left">
                    <div className="bi-avatar">{booking.customer?.name?.charAt(0) || 'C'}</div>
                  </div>
                  <div className="bi-body">
                    <div className="bi-top">
                      <div>
                        <div className="bi-service">{booking.service?.title}</div>
                        <div className="bi-provider">Customer: {booking.customer?.name} • 📞 {booking.customer?.phone || 'N/A'}</div>
                        <div className="bi-meta">📅 {new Date(booking.bookingDate).toLocaleDateString()} &nbsp;•&nbsp; 🕐 {booking.timeSlot} &nbsp;•&nbsp; 📍 {booking.address}, {booking.city}</div>
                        {booking.notes && <div style={{ fontSize: '12px', color: 'var(--gray-500)', marginTop: '4px' }}>📝 {booking.notes}</div>}
                      </div>
                      <div className="bi-right">
                        <span className="bi-status" style={{ background: s.bg, color: s.color }}>{s.label}</span>
                        <div className="bi-amount">Rs. {booking.providerEarning?.toFixed(0)}</div>
                        <div className="bi-commission">Your earning (after 15% fee)</div>
                      </div>
                    </div>
                    {booking.status === 'pending' && (
                      <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        <button onClick={() => updateBookingStatus(booking._id, 'confirmed')}
                          style={{ padding: '6px 16px', background: 'var(--primary-light)', color: 'var(--primary)', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                          ✅ Accept
                        </button>
                        <button onClick={() => updateBookingStatus(booking._id, 'cancelled')}
                          style={{ padding: '6px 16px', background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                          ❌ Decline
                        </button>
                      </div>
                    )}
                    {booking.status === 'confirmed' && (
                      <button onClick={() => updateBookingStatus(booking._id, 'completed')}
                        style={{ marginTop: '10px', padding: '6px 16px', background: '#F0FDF4', color: '#16A34A', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
                        ✔️ Mark as Completed
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProviderDashboard;
